import { captureException } from '@sentry/react';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { collection, DocumentSnapshot, onSnapshot, query, QuerySnapshot } from '@firebase/firestore';

import { db } from '../firebase-config';

/**
 * @typedef {Object} CatalogueState
 * @property {Record<string, CatalogueItem>} data - The catalogue data
 * @property {boolean} loading - Whether the catalogue is initially loading
 * @property {Error | null} error - The error if the catalogue fails to load
 * @property {boolean} enabled - Whether the catalogue is enabled
 */

/**
 * @typedef UseCatalogueReturn
 * @property {Record<string, CatalogueItem>} data - The catalogue data
 * @property {boolean} loading - Whether the catalogue is initially loading
 * @property {Error | null} error - The error if the catalogue fails to load
 * @property {boolean} enabled - Whether the catalogue is enabled
 * @property {() => void} start - Function to manually start the subscription (only available when lazy=true)
 */

/**
 * @typedef {object} DocumentChange
 * @property {import('@firebase/firestore').DocumentChange['type']} type
 * @property {DocumentSnapshot<CatalogueItem>} doc
 */

/**
 * @typedef {Object} SetLoadingAction
 * @property {'SET_LOADING'} type
 * @property {boolean} loading
 */

/**
 * @typedef {Object} SetDataSuccessAction
 * @property {'SET_DATA_SUCCESS'} type
 * @property {Parameters<React.Dispatch<React.SetStateAction<Record<string, CatalogueItem>>>>[0]} data
 */

/**
 * @typedef {Object} SetErrorAction
 * @property {'SET_ERROR'} type
 * @property {Error} error
 */

/**
 * @typedef {Object} ResetAction
 * @property {'RESET'} type
 */

/**
 * @typedef {SetLoadingAction | SetDataSuccessAction | SetErrorAction | ResetAction} CatalogueAction
 */

/**
 * Creates the initial state for the catalogue hook
 * @param {object} params
 * @param {boolean} params.enabled
 * @param {boolean} [params.lazy]
 * @returns {CatalogueState}
 */
const createInitialState = ({ enabled, lazy = false }) => ({
  data: /** @type {Record<string, CatalogueItem>} */ ({}),
  loading: enabled && !lazy,
  error: /** @type {Error | null} */ (null),
  enabled,
});

/**
 * Reducer function for managing catalogue state
 * @param {CatalogueState} state
 * @param {CatalogueAction} action
 * @returns {CatalogueState}
 */
const catalogueReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.loading,
      };
    case 'SET_DATA_SUCCESS':
      return {
        ...state,
        data: typeof action.data === 'function' ? action.data(state.data) : action.data,
        loading: false,
        error: null,
      };
    case 'SET_ERROR':
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case 'RESET':
      return createInitialState({ enabled: state.enabled, lazy: false });
    default:
      return state;
  }
};

/**
 * Processes a single document change and applies it to the data object
 * @param {Record<string, CatalogueItem>} data
 * @param {DocumentChange} change
 * @returns {Record<string, CatalogueItem>}
 */
const applyDocumentChange = (data, { type, doc }) => {
  if (type === 'removed') {
    const { [doc.id]: _, ...rest } = data;

    return rest;
  }

  const item = /** @type {CatalogueItem} */ ({ ...doc.data(), id: doc.id });

  // 'added' or 'modified'
  return { ...data, [doc.id]: item };
};

/**
 * Processes multiple document changes in batch
 * @param {object} params
 * @param {Record<string, CatalogueItem>} params.currentData
 * @param {DocumentChange[]} params.changes
 * @returns {Record<string, CatalogueItem>}
 */
const processBatchedChanges = ({ currentData, changes }) => changes.reduce(applyDocumentChange, currentData);

/**
 * Creates a debounced function that batches multiple calls
 *
 * @param {(data: DocumentChange[]) => void} callback
 * @param {number} delay
 * @returns {{ execute: (data: DocumentChange[]) => void, cleanup: () => void }} - Object with execute function and cleanup function
 */
const createDebouncedBatcher = (callback, delay) => {
  let timeoutId = null;
  const pendingData = /** @type {Map<string, DocumentChange>} */ (new Map());

  /**
   * @param {DocumentChange[]} data
   */
  const execute = (data) => {
    data.forEach(({ doc, type }) => {
      pendingData.set(doc.id, { doc, type });
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      callback(Array.from(pendingData.values()));
      pendingData.clear();
      timeoutId = null;
    }, delay);
  };

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    pendingData.clear();
  };

  return { execute, cleanup };
};

/**
 * Processes Firebase snapshot changes and extracts document changes
 * @param {QuerySnapshot} querySnapshot
 * @returns {DocumentChange[]}
 */
const extractDocumentChanges = (querySnapshot) => {
  const changes = querySnapshot.docChanges();

  return changes.map(({ type, doc: untypedDoc }) => ({
    type,
    doc: /** @type {DocumentSnapshot<CatalogueItem>} */ (untypedDoc),
  }));
};

/**
 * @param {Object} props
 * @param {boolean} [props.enabled]
 * @param {boolean} [props.lazy] - If true, delays subscription until start() is called
 * @param {number} [props.debounceMs] - Debounce delay in milliseconds (default: 1000ms)
 * @returns {UseCatalogueReturn} - The catalogue data.
 */
export const useProductCatalogue = ({ enabled = true, lazy = false, debounceMs = 1000 } = {}) => {
  const [state, dispatch] = useReducer(catalogueReducer, { enabled, lazy }, createInitialState);

  const startedRef = useRef(false);
  const cleanupRef = useRef(/** @type {(() => void) | null} */ (null));
  const unsubscribeRef = useRef(/** @type {(() => void) | null} */ (null));

  const startSubscription = useCallback(() => {
    if (!enabled || startedRef.current) {
      return;
    }

    startedRef.current = true;
    dispatch({ type: 'SET_LOADING', loading: true });

    const debouncedBatcher = createDebouncedBatcher((allChanges) => {
      dispatch({
        type: 'SET_DATA_SUCCESS',
        data: (prev) => processBatchedChanges({ currentData: prev, changes: allChanges }),
      });
    }, debounceMs);

    cleanupRef.current = debouncedBatcher.cleanup;

    unsubscribeRef.current = onSnapshot(
      query(collection(db, 'catalogue')),
      (querySnapshot) => {
        const changes = extractDocumentChanges(querySnapshot);

        // No changes to process
        if (changes.length === 0) {
          dispatch({ type: 'SET_LOADING', loading: false });
          return;
        }

        debouncedBatcher.execute(changes);
      },
      (error) => {
        captureException(error, {
          tags: {
            issueIn: 'useProductCatalogue',
          },
          extra: {
            enabled,
            debounceMs,
          },
        });

        dispatch({ type: 'SET_ERROR', error });
      },
    );
  }, [enabled, debounceMs]);

  const cleanup = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    startedRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return cleanup;
    }

    // If not lazy, start immediately
    if (!lazy) {
      startSubscription();
    }

    return cleanup;
  }, [enabled, lazy, startSubscription, cleanup]);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    enabled: state.enabled,
    start: startSubscription,
  };
};
